import { useEffect } from 'react';
import { db } from '../firebase';
import {
  collection,
  doc,
  onSnapshot,
  query,
  orderBy,
  setDoc
} from 'firebase/firestore';

// Encapsulates the Firestore data subscriptions that were previously inline in StudentPortfolioApp
// NOTE: This hook is designed to be a drop-in extraction: it accepts the existing setters
// so the rest of StudentPortfolioApp.js code remains unchanged.
export const useAppData = ({
  currentUser,
  loadingAuth,
  setters: { setPosts, setComments, setTagFeedbacks, setTags, setStudents, setAllUsers },
  state
}) => {
  useEffect(() => {
    // Match original behavior: wait for auth to finish
    if (loadingAuth) {
      return;
    }

    // If no user, clear data and stop
    if (!currentUser) {
      setPosts([]);
      setComments([]);
      setTagFeedbacks([]);
      setTags([]);
      setStudents([]);
      setAllUsers([]);
      return;
    }

    // Helper function to handle permission errors gracefully
    const handleSnapshotError = (error, collectionName, setter) => {
      if (error.code === 'permission-denied') {
        console.warn(`Permission denied for ${collectionName}. Setting empty array.`);
        setter([]);
      } else {
        console.error(`Error fetching ${collectionName}:`, error);
      }
    };

    // --- Listen for Posts ---
    const qPosts = query(collection(db, 'posts'), orderBy('timestamp', 'desc'));
    const unsubscribePosts = onSnapshot(
      qPosts,
      (snapshot) => {
        const postsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPosts(postsData);
      },
      (error) => handleSnapshotError(error, 'posts', setPosts)
    );

    // --- Listen for Comments ---
    const qComments = query(collection(db, 'comments'), orderBy('timestamp', 'asc'));
    const unsubscribeComments = onSnapshot(
      qComments,
      (snapshot) => {
        const commentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setComments(commentsData);
      },
      (error) => handleSnapshotError(error, 'comments', setComments)
    );

    // --- Listen for Tag Feedback ---
    const qTagFeedback = query(collection(db, 'tagFeedback'), orderBy('timestamp', 'asc'));
    const unsubscribeTagFeedback = onSnapshot(
      qTagFeedback,
      (snapshot) => {
        const tfData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTagFeedbacks(tfData);
      },
      (error) => handleSnapshotError(error, 'tagFeedback', setTagFeedbacks)
    );

    // --- Listen for Tags (global list) ---
    const tagsDocRef = doc(db, 'settings', 'tags');
    const unsubscribeTags = onSnapshot(
      tagsDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setTags(docSnap.data().list || []);
        } else {
          // If the document doesn't exist, create it with initial tags
          setDoc(tagsDocRef, { list: ["homework", "project", "question", "reflection"] }, { merge: true })
            .then(() => setTags(["homework", "project", "question", "reflection"]))
            .catch(error => {
              if (error.code === 'permission-denied') {
                console.warn('Permission denied for tags. Using default tags.');
                setTags(["homework", "project", "question", "reflection"]);
              } else {
                console.error("Error initializing tags doc:", error);
              }
            });
        }
      },
      (error) => {
        if (error.code === 'permission-denied') {
          console.warn('Permission denied for tags. Using default tags.');
          setTags(["homework", "project", "question", "reflection"]);
        } else {
          console.error("Error fetching tags:", error);
        }
      }
    );

    // --- Listen for Students (for teacher view) ---
    const qStudents = query(collection(db, 'users'));
    const unsubscribeStudents = onSnapshot(
      qStudents,
      (snapshot) => {
        const allUsersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllUsers(allUsersData);
        const studentsData = allUsersData
          .filter(user => user.role === 'student')
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setStudents(studentsData);
      },
      (error) => handleSnapshotError(error, 'students', (data) => {
        setAllUsers([]);
        setStudents([]);
      })
    );

    return () => {
      unsubscribePosts();
      unsubscribeComments();
      unsubscribeTagFeedback();
      unsubscribeTags();
      unsubscribeStudents();
    };
  }, [currentUser, loadingAuth, setPosts, setComments, setTagFeedbacks, setTags, setStudents, setAllUsers]);

  // Expose current values for convenience if needed by callers
  return state;
};

export default useAppData;


